import React, {Fragment} from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';

import PendingIcon from '@mui/icons-material/Pending';
import DangerousIcon from '@mui/icons-material/Dangerous';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import usePortfolioBalance from '../../hooks/usePortfolioBalance';
import {
  isFiatGateway,
  formatUsd,
  formatCryptoAmount,
  getCoinUsd,
} from '../../utils/balanceUtils';

const Transactions = () => {

  const addressText = (address) => {
    let text = String(address) || address;
    let last6 = text.slice(text.length - 6);
    let first3 = text.substring(0,6);
    return first3+'***'+last6;
  }

    const fundings = useSelector((state) => state.wallet.fundings);
    const depayTransactions = useSelector((state) => state.history.depayTransactions);
    const withdrawals = useSelector((state) => state.history.withdrawals);

    const { coinRates } = usePortfolioBalance();

    const allTransactions = fundings.concat(depayTransactions);

    const combinedTransactions = allTransactions.sort((b, a) => {
        return (a.created_at).localeCompare((b.created_at))
    })

    const pluswithdrawal = combinedTransactions.concat(withdrawals);
    const finalHistory = pluswithdrawal.sort((b, a) => {
      return (a.created_at).localeCompare((b.created_at))
  })

  /** @returns {{ primary: string, secondary: string|null }} */
  const formatTransactionAmounts = (tx) => {
    const symbol = tx.gateway || tx.coin_name || tx.source;
    const raw = parseFloat(tx.local_amount ?? tx.amount) || 0;

    if (tx.coin_name && tx.local_amount != null && !tx.gateway) {
      return {
        primary: formatUsd(raw),
        secondary: tx.coin_name,
      };
    }

    if (tx.gateway) {
      const isCrypto = symbol && !isFiatGateway(symbol);

      if (isCrypto) {
        if (tx.status === 'processing') {
          return {
            primary: formatUsd(raw),
            secondary: `USD deposit · ${symbol}`,
          };
        }

        const usdValue =
          tx.amount_usd != null && tx.amount_usd !== ''
            ? parseFloat(tx.amount_usd)
            : getCoinUsd(symbol, raw, coinRates);

        return {
          primary: `${formatCryptoAmount(raw)} ${symbol}`,
          secondary: `≈ ${formatUsd(usdValue)} USD`,
        };
      }

      return {
        primary: formatUsd(raw),
        secondary: symbol,
      };
    }

    if (tx.source && !isFiatGateway(tx.source) && (tx.address != null || tx.coin_name)) {
      const coin = tx.coin_name || tx.source;
      const usdValue =
        tx.amount_usd != null && tx.amount_usd !== ''
          ? parseFloat(tx.amount_usd)
          : getCoinUsd(coin, raw, coinRates);
      return {
        primary: `${formatCryptoAmount(raw)} ${coin}`,
        secondary: `≈ ${formatUsd(usdValue)} USD`,
      };
    }

    return {
      primary: formatUsd(raw),
      secondary: tx.coin_name || null,
    };
  };

  const statusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved' || s === 'successful' || s === 'paid') return 'success.main';
    if (s === 'declined') return 'error.main';
    return '#fcaa00';
  };

  const getTransactionDirection = (tx) => {
    if (tx.address != null && !tx.gateway && (tx.source || tx.coin_name)) {
      return 'withdrawal';
    }
    return 'deposit';
  };

  const avatarBg = (status, direction) => {
    const s = (status || '').toLowerCase();
    if (s === 'declined') return '#dd0a22';
    if (s === 'pending' || s === 'processing') return '#f7a21d';
    if (s === 'completed') return '#6b7280';
    if (direction === 'withdrawal') return '#c45c4a';
    return '#37dc8a';
  };

  const renderTransactionAvatar = (tx) => {
    const direction = getTransactionDirection(tx);
    const s = (tx.status || '').toLowerCase();
    const DirectionIcon = direction === 'withdrawal' ? ArrowDownwardIcon : ArrowUpwardIcon;

    if (s === 'declined') {
      return (
        <Avatar sx={{ bgcolor: avatarBg(s, direction) }}>
          <DangerousIcon />
        </Avatar>
      );
    }
    if (s === 'pending' || s === 'processing') {
      return (
        <Avatar sx={{ bgcolor: avatarBg(s, direction) }}>
          <PendingIcon />
        </Avatar>
      );
    }
    if (s === 'completed') {
      return (
        <Avatar sx={{ bgcolor: avatarBg(s, direction) }}>
          <DoneAllIcon />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ bgcolor: avatarBg(s, direction) }}>
        <DirectionIcon />
      </Avatar>
    );
  };

  return (
    <div>
{finalHistory.length > 0 && (
<List sx={{ width: '100%' }}>

{finalHistory.map(transaction => {
  const { primary, secondary } = formatTransactionAmounts(transaction);
  const label = transaction?.after_block || transaction.gateway || transaction.coin_name || transaction.source;
  const direction = getTransactionDirection(transaction);
  const isDeposit = direction === 'deposit';

  return (
   <Fragment key={`${transaction.id}-${transaction.transaction_id || transaction.uuid || ''}`}>
  <ListItem alignItems="flex-start">
    
  <ListItemAvatar>
    {renderTransactionAvatar(transaction)}
  </ListItemAvatar>

  <ListItemText
    primary={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography component="span" variant="body1" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{
            px: 0.75,
            py: 0.2,
            borderRadius: 0.75,
            fontWeight: 700,
            fontSize: 10,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            bgcolor: isDeposit ? 'rgba(55, 220, 138, 0.2)' : 'rgba(196, 92, 74, 0.25)',
            color: isDeposit ? '#37dc8a' : '#e88b7a',
          }}
        >
          {isDeposit ? 'Deposit' : 'Withdrawal'}
        </Typography>
      </Box>
    }
    secondary={
      <React.Fragment>
        <Typography
          component="span"
          variant="caption"
          display="block"
          color="text.primary"
        >
          { addressText(transaction?.uuid || transaction.transaction_id || transaction.address) }
          {transaction.destination_tag && (
            <> · Tag: {transaction.destination_tag}</>
          )}
        </Typography>
      </React.Fragment>
    }
  />
   

    <Box edge="end" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" display="block">
            {secondary}
          </Typography>
        )}
        <Typography sx={{ color: statusColor(transaction.status), fontSize: 13, mt: 0.5 }}>
          {transaction.status}
        </Typography>
    </Box>

  
</ListItem>
<Divider component="li" variant="inset" />
</Fragment>
);
})}
</List>
)}
    </div>
  )
}

export default Transactions
